import axios from "axios";

// All backend calls go through REACT_APP_BACKEND_URL and are /api prefixed
// so the Kubernetes ingress routes them to FastAPI.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const COMPANY_ID = "cardiac-solutions";

const http = axios.create({ baseURL: API, timeout: 12000 });

export async function fetchState(companyId = COMPANY_ID) {
  try {
    const { data } = await http.get(`/state/${companyId}`);
    return data?.data ?? null;
  } catch (e) {
    if (e?.response?.status === 404) return null;
    // Network / backend down -> caller falls back to localStorage or seed.
    throw e;
  }
}

export async function saveState(data, companyId = COMPANY_ID) {
  const { data: res } = await http.put(`/state/${companyId}`, { data });
  return res;
}

export async function resetServerState(companyId = COMPANY_ID) {
  await http.delete(`/state/${companyId}`);
}

// --- Gmail Support Inbox (Nora) ---------------------------------------------
export const gmail = {
  status: () => http.get("/gmail/status").then((r) => r.data),
  loginUrl: () => `${API}/oauth/gmail/login?redirect_base=${encodeURIComponent(BACKEND_URL)}`,
  disconnect: () => http.post("/gmail/disconnect").then((r) => r.data),
  messages: () => http.get("/gmail/messages").then((r) => r.data),
  message: (id) => http.get(`/gmail/message/${id}`).then((r) => r.data),
  draft: (id) => http.post("/gmail/draft", { id }, { timeout: 30000 }).then((r) => r.data),
  reply: (id, body) => http.post("/gmail/reply", { id, body }, { timeout: 30000 }).then((r) => r.data),
  setSettings: (obj) => http.post("/gmail/settings", obj).then((r) => r.data),
  process: () => http.post("/gmail/process", {}, { timeout: 60000 }).then((r) => r.data),
  unreadCount: () => http.get("/gmail/unread-count").then((r) => r.data),
  context: () => http.get("/gmail/context").then((r) => r.data),
  voiceReply: (email_subject, reply_body) => http.post("/gmail/voice-reply", { email_subject, reply_body }, { timeout: 30000 }).then((r) => r.data),
  sendToUser: (recipient, subject, body) => http.post("/gmail/send-to-user", { recipient, subject, body }, { timeout: 30000 }).then((r) => r.data),
  directory: () => http.get("/directory", { withCredentials: true }).then((r) => r.data),
};

// --- Knowledge base (Associate Training + About Company) --------------------
const authed = { withCredentials: true };
export const knowledge = {
  list: (scope, associateId) =>
    http.get(`/knowledge`, { params: { scope, associate_id: associateId }, ...authed }).then((r) => r.data),
  upload: (fileObj, scope, associateId, onProgress) => {
    const fd = new FormData();
    fd.append("file", fileObj);
    fd.append("scope", scope);
    if (associateId) fd.append("associate_id", associateId);
    return http
      .post(`/knowledge/upload`, fd, {
        withCredentials: true,
        timeout: 120000,
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded / e.total) * 100)),
      })
      .then((r) => r.data);
  },
  addLink: (url, scope, associateId) =>
    http.post(`/knowledge/link`, { url, scope, associate_id: associateId }, { ...authed, timeout: 60000 }).then((r) => r.data),
  remove: (id) => http.delete(`/knowledge/${id}`, authed).then((r) => r.data),
};

// --- Admin: authorized logins ----------------------------------------------
export const twilio = {
  status: () => http.get(`/twilio/status`, authed).then((r) => r.data),
  outbound: (phone_number, recipient_name, purpose) => http.post(`/twilio/outbound`, { phone_number, recipient_name, purpose }, authed).then((r) => r.data),
  sendSms: (phone_number, contact_name, message) => http.post(`/twilio/send-sms`, { phone_number, contact_name, message }, authed).then((r) => r.data),
  scheduleSms: (phone_number, contact_name, message, send_at) => http.post(`/twilio/schedule-sms`, { phone_number, contact_name, message, send_at }, authed).then((r) => r.data),
  scheduled: () => http.get(`/twilio/scheduled`, authed).then((r) => r.data),
  cancelScheduled: (id) => http.delete(`/twilio/scheduled/${id}`, authed).then((r) => r.data),
  active: () => http.get(`/twilio/active`, authed).then((r) => r.data),
  logs: () => http.get(`/twilio/logs`, authed).then((r) => r.data),
};

export const voice = {
  prefs: () => http.get(`/voice/prefs`, authed).then((r) => r.data),
  setPref: (employee_id, voice) => http.put(`/voice/prefs`, { employee_id, voice }, authed).then((r) => r.data),
};

export const contacts = {
  list: () => http.get(`/contacts`, authed).then((r) => r.data),
  add: (name, phone, email, notes) => http.post(`/contacts`, { name, phone, email, notes }, authed).then((r) => r.data),
  update: (id, patch) => http.put(`/contacts/${id}`, patch, authed).then((r) => r.data),
  remove: (id) => http.delete(`/contacts/${id}`, authed).then((r) => r.data),
  bulk: (list) => http.post(`/contacts/bulk`, { contacts: list }, authed).then((r) => r.data),
};

export const logins = {
  list: () => http.get(`/logins`, authed).then((r) => r.data),
  add: (email, name, role) => http.post(`/logins`, { email, name, role }, authed).then((r) => r.data),
  remove: (email) => http.delete(`/logins/${encodeURIComponent(email)}`, authed).then((r) => r.data),
};
